<template>
  <div>
    <result-list-edit :is-edit-result-sidebar-active.sync="isEditResultSidebarActive"
      :proposedDecisionOptions="proposedDecisionOptions" :item="item" @refetch-data="refetchData" />

    <!-- Filters -->
    <results-list-filters :role-filter.sync="roleFilter" :level-filter.sync="levelFilter"
      :proposedDecisionFilter.sync="proposedDecisionFilter" :discussionFilter.sync="discussionFilter"
      :role-options="roleOptions" :level-options="levelOptions" :proposedDecisionOptions="proposedDecisionOptions"
      :discussionOptions="discussionOptions" />

    <div class="d-flex align-items-center mb-2">
      <div class="d-flex align-items-center" style="flex: 1 1 0; gap: 8px;">
        <b-button variant="outline-info" size="sm" @click="showColumnInfo = true">
          <feather-icon icon="HelpCircleIcon" size="18" class="mr-1" />
          <span>What do these columns mean?</span>
        </b-button>
        <b-button v-if="selectedCycle" variant="outline-primary" size="sm" @click="downloadExcel" :disabled="isExporting">
          <feather-icon icon="DownloadIcon" size="18" class="mr-1" />
          <span>{{ isExporting ? 'Exporting...' : 'Download Excel' }}</span>
        </b-button>
      </div>
      <div class="d-flex align-items-center justify-content-center"
        style="flex: 0 1 auto; width: 100%; position: absolute; left: 0; right: 0; pointer-events: none;">
        <span v-if="activeCycleID" style="pointer-events: auto;" @click="showCurrentCycle">
          <b-button variant="outline-success">
            <b>Show Current Cycle</b>
          </b-button>
        </span>
      </div>
    </div>
    <b-modal v-model="showColumnInfo" title="Results Table Columns Explained" hide-footer size="xl"
      content-class="column-info-modal">
      <div class="table-responsive">
        <table class="table table-hover column-info-table mb-0">
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
              <th class="text-center">Modifiability</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="col in tableColumnInfo" :key="col.key">
              <td><b>{{ col.label }}</b></td>
              <td>{{ col.description }}</td>
              <td class="text-center">
                <span v-if="col.modifiable === true">
                  <b-badge variant="success" pill><feather-icon icon="Edit2Icon" size="16"
                      class="mr-1" />Modifiable</b-badge>
                </span>
                <span v-else>
                  <b-badge variant="secondary" pill><feather-icon icon="LockIcon" size="16" class="mr-1" />Not
                    Modifiable</b-badge>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-center w-100 text-dark">
        <b class="text-warning">NOTE:</b> The <b>Advice</b> column's content can be <b>seen by the employee</b> on their
        own result's page.
      </div>
    </b-modal>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select v-model="limit" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label>entries</label>
            <span class="ml-1" v-if="selectedCycleData">
              <b-badge variant="light-primary" pill>
                <b>{{ formatDate(selectedCycleData.startDate) }}</b> to <b>{{
                  formatDate(selectedCycleData.deadline)
                  }}
                </b>
              </b-badge>
            </span>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="3">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block" placeholder="Search..." />
            </div>
          </b-col>
          <b-col cols="12" md="3">
            <v-select :value="selectedCycle" v-model="selectedCycle" :options="cycleOptions" placeholder="Select Cycle"
              class="w-100" :reduce="val => val.value" :clearable="false" />
          </b-col>
        </b-row>
      </div>

      <b-table ref="refResultListTable" class="position-relative" :items="fetchResults" responsive
        :fields="dynamicTableColumns" primary-key="id" :sort-by.sync="sortBy" show-empty
        empty-text="No matching records found" :sort-desc.sync="sortOrderSync">
        <template #table-busy>
          <div class="text-center my-3">
            <b-spinner style="width: 3rem; height: 3rem;" variant="primary" label="Loading..."></b-spinner>
          </div>
        </template>

        <!-- Column: Result -->
        <template #cell(result)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar size="32" :src="data.item.user.avatar" :text="avatarText(data.item.user.fullName)"
                :variant="`light-${resolveResultRoleVariant(data.item.user.role)}`" />
            </template>
            <b-link class="font-weight-bold d-block text-nowrap lead" @click="goToUserResults(data.item.user)">{{
              data.item.user.fullName }}</b-link>
            <div class="text-secondary h5 my-0 py-0">{{ data.item.user.level.name }} <small> ({{
                data.item.user.level.category
                }})</small></div>
            <!-- <small class="">{{ data.item.user.email }}</small> -->
          </b-media>
        </template>

        <template #cell(timeInRole)="data">
          <span class=""><b>{{ timeInRole(data.item.user.roleStartDate, selectedCycleData && selectedCycleData.startDate ? selectedCycleData.startDate : undefined) }}</b></span>
        </template>

        <template #cell(advisor)="data">
          <b-media v-if="data.item.user.advisor" vertical-align="center">
            <b-link class="font-weight-bold d-block text-nowrap" @click="goToUserResults(data.item.user.advisor)">
              {{ data.item.user.advisor.fullName }}
            </b-link>
            <!-- <span class="text-secondary ">{{ data.item.user.advisor.email }}</span> -->
          </b-media>
          <span v-else><b-badge variant="light-secondary">N/A</b-badge> </span>
        </template>

        <template #cell(aggregatedSelfEval)="data">
          <span v-if="data.item.aggregatedSelfEval">
            <!-- <small><b>{{data.item.aggregatedSelfEval.toFixed(2)}}</b></small> -->
            <b-badge variant="secondary" pill>{{ data.item.aggregatedSelfEval.toFixed(2) }}</b-badge>
          </span>
          <span v-else><b-badge variant="light-secondary" pill>N/A</b-badge></span>
        </template>
        <template #cell(aggregatedType1)="data">
          <span v-if="data && data.item.numberOfType1 >= resultsNumberThreshold && data.item.aggregatedType1"
            v-b-tooltip.hover.top="getTooltip(data.item.user.level.category, 'type1')">
            <span v-if="!data.item.fixedScale">
              <b-badge :variant="getBadgeVariant(data.item.aggregatedType1, data.item.expectedScore)">
                {{ data.item.aggregatedType1.toFixed(2) }}
              </b-badge>
            </span>
            <span v-else>
              <b-badge variant="secondary">
                {{ data.item.aggregatedType1.toFixed(2) }}
              </b-badge>
            </span>
          </span>
          <span v-else v-b-tooltip.hover.top="getTooltip(data.item.user.level.category, 'type1')">
            <b-badge variant="light-secondary" pill>N/A</b-badge>
          </span>
        </template>

        <template #cell(aggregatedType2)="data">
          <span v-if="data && data.item.numberOfType2 >= resultsNumberThreshold && data.item.aggregatedType2"
            v-b-tooltip.hover.top="getTooltip(data.item.user.level.category, 'type2')">
            <span v-if="!data.item.fixedScale">
              <b-badge v-if="!data.item.fixedScale" :variant="getBadgeVariant(data.item.aggregatedType2, data.item.expectedScore)">
                {{ data.item.aggregatedType2.toFixed(2) }}
              </b-badge>
            </span>
            <span v-else>
              <b-badge variant="secondary">
                {{ data.item.aggregatedType2.toFixed(2) }}
              </b-badge>
            </span>
          </span>
          <span v-else v-b-tooltip.hover.top="getTooltip(data.item.user.level.category, 'type2')">
            <b-badge variant="light-secondary" pill>N/A</b-badge>
          </span>
        </template>

        <!-- Column: Total Score -->
        <template #cell(expectedScore)="data">
          <b-badge v-if="!data.item.fixedScale" :variant="getExpectedScoreVariant(data.item.expectedScore)">
            {{ data.item.expectedScore }}
          </b-badge>
          <b-badge v-else variant="light-primary">
            Out of {{ data.item.fixedScale }}
          </b-badge>
        </template>

        <!-- Column: Total Score -->
        <template #cell(aggregatedScore)="data">
          <span class="text-center"
            v-if="data && data.item.numberOfType1 >= resultsNumberThreshold && data.item.numberOfType2 >= resultsNumberThreshold && data.item.aggregatedScore">
            <span v-if="!data.item.fixedScale">
              <b-badge :variant="getBadgeVariant(data.item.aggregatedScore, data.item.expectedScore)">
                {{ data.item.aggregatedScore.toFixed(2) }}
                <small>
                  ({{ (data.item.aggregatedScore > data.item.expectedScore? "+" : "")
                  +
                  (parseFloat(data.item.aggregatedScore) -
                  parseFloat(data.item.expectedScore)).toFixed(2).toString()}})
                </small>
              </b-badge>
            </span>
            <span v-else>
              <b-badge variant="secondary">
                {{ data.item.aggregatedScore }}
              </b-badge>
            </span>
          </span>
          <span v-else><b-badge variant="light-secondary" pill>N/A</b-badge></span>
        </template>

        <!-- Column: Competency Scores -->
        <template #cell(competencyScores)="data">
          <span :class="`flag-${resolveCompetencyFlag(data.item.competencyScores)}`"></span>
        </template>

        <!-- Column: Discussion -->
        <template #cell(discussion)="data">
          <span
            :class="data.item.discussion ? 'status-dot status-dot--active' : 'status-dot status-dot--inactive'"></span>
        </template>

        <template #cell(proposedDecision)="data">
          <span>
            <b-badge :variant="resolveResultDecisionVariant(data.item.proposedDecision)">
              {{
              data.item.proposedDecision }}
            </b-badge></span>
        </template>

        <!-- Column: Comments -->
        <template #cell(comments)="data">
          <span v-if="data.item.comments"> {{ data.item.comments }}</span>
          <span v-else><b-badge variant="light-secondary">N/A</b-badge></span>
        </template>

        <template #cell(advice)="data">
          <span v-if="data.item.advice"> {{ data.item.advice }}</span>
          <span v-else><b-badge variant="light-secondary">N/A</b-badge></span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data" v-if="isAdmin">
          <feather-icon icon="EditIcon" class="edit-button ml-1" @click="editItem(data.item)" />
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }}
              entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="page" :total-rows="totalResults" :per-page="limit" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSpinner, VBTooltip, BModal, BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import store from '@/store'
import { ref, onUnmounted, computed, onMounted, getCurrentInstance, watch } from '@vue/composition-api'
import ResultsListFilters from './ResultsListFilters.vue'
import useResultsList from './useResultsList'
import resultStoreModule from '../resultStoreModule'
import ResultListEdit from './ResultListEdit.vue'
import flatPickr from 'vue-flatpickr-component'
import { timeInRole } from '../../utils'

export default {
  components: {
    ResultsListFilters,
    ResultListEdit,
    BAlert,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
    flatPickr,
    BModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const showColumnInfo = ref(false)
    const RESULT_APP_STORE_MODULE_NAME = 'app-result'
    // const proposedDecisionOptions = ref([])
    const levelOptions = ref([])
    const cycleOptions = ref([]) // New cycle options
    const proposedDecisionOptions = ref([])
    const allCycles = ref([])
    const activeCycleID = ref(null)
    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'User', value: 'user' },
    ]
    const discussionOptions = [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ]

    const item = ref(null)
    const isExporting = ref(false)

    const isEditResultSidebarActive = ref(false)

    const { proxy } = getCurrentInstance()

    const resultsNumberThreshold = 0

    // Register module
    if (!store.hasModule(RESULT_APP_STORE_MODULE_NAME)) store.registerModule(RESULT_APP_STORE_MODULE_NAME, resultStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(RESULT_APP_STORE_MODULE_NAME)) store.unregisterModule(RESULT_APP_STORE_MODULE_NAME)
    })

    const {
      fetchResults,
      tableColumns,
      limit,
      page,
      totalResults,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      sortOrder,
      refResultListTable,
      refetchData,

      // UI
      resolveResultRoleVariant,
      resolveResultRoleIcon,
      resolveResultStatusVariant,
      resolveResultDecisionVariant,
      resolveCompetencyFlag,

      // Extra Filters
      roleFilter,
      levelFilter,
      proposedDecisionFilter,
      discussionFilter,

      selectedCycle,
      isAdmin,
      tableColumnInfo,
    } = useResultsList()

    const types = ref([])


    onMounted(() => {
      // Fetch cycleOptions
      proxy.$http.get('/cycles')
        .then(response => {
          const cycles = response.data.cycles
          allCycles.value = cycles
          cycleOptions.value = cycles.map(cycle => ({ label: cycle.title, value: cycle._id }))
          selectedCycle.value = cycleOptions.value[0].value
        })
        .catch(error => console.error('Error fetching cycles:', error))


      proxy.$http.get('/cycles/isActive').then(response => {
        const isCycleActive = response.data.isActive
        if (isCycleActive) {
          proxy.$http.get('/cycles/active')
            .then(response => {
              const currentCycle = response.data
              activeCycleID.value = currentCycle._id
              selectedCycle.value = currentCycle._id
            })
            .catch(error => console.error('Error fetching active cycle:', error))
        }
      })

      proxy.$http.get('/levels')
        .then(response => {
          const levels = response.data.levels
          levelOptions.value = levels.map(level => ({ label: level.name, value: level._id }))
        })
        .catch(error => console.error('Error fetching level options:', error))

      proxy.$http.get('/results/proposedDecisionOptions')
        .then(response => {
          proposedDecisionOptions.value = Object.keys(response.data).map(key => ({
            label: response.data[key].charAt(0).toUpperCase() + response.data[key].slice(1),
            value: response.data[key]
          }));
        })
        .catch(error => console.error('Error fetching proposed decision options:', error))

      proxy.$http.get('/questionnaires/types')
        .then(response => {
          types.value = response.data
        })
        .catch(error => console.error('Error fetching type names:', error))
    })

    const sortOrderSync = computed({
      get: () => sortOrder.value === 'desc',
      set: (newVal) => {
        sortOrder.value = newVal ? 'desc' : 'asc'
      }
    })

    const dynamicTableColumns = computed(() => {
      const columns = tableColumns
      // Remove actions column if not admin or if there is no active cycle
      if (!isAdmin.value || selectedCycle.value != activeCycleID.value) {
        return columns.filter(col => col.key !== 'actions')
      }
      return columns
    })

    const selectedCycleData = computed({
      get: () => {
        if (allCycles.value && selectedCycle.value) {
          const data = allCycles.value.filter(function (cycle) {
            return cycle._id == selectedCycle.value
          });
          return data[0]
        }
        return null
      },
    })

  return {
      // Sidebar
      isEditResultSidebarActive,

      fetchResults, // Updated to use fetchResultsWithCycle
      tableColumns,
      dynamicTableColumns,
      limit,
      page,
      totalResults,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      sortOrder,
      sortOrderSync,
      refResultListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveResultRoleVariant,
      resolveResultRoleIcon,
      resolveResultStatusVariant,
      resolveResultDecisionVariant,

      roleOptions,
      levelOptions,
      cycleOptions,
      proposedDecisionOptions,
      discussionOptions,
      selectedCycle,
      selectedCycleData,
      activeCycleID,
      allCycles,
      resultsNumberThreshold,

      // Extra Filters
      roleFilter,
      levelFilter,
      proposedDecisionFilter,
      discussionFilter,
      item,
      types,
      isAdmin,
      resolveCompetencyFlag,
      showColumnInfo,
      tableColumnInfo,
      isExporting,
    }
  },
  methods: {
    editItem(item) {
      this.item = item
      this.isEditResultSidebarActive = true
    },
    showCurrentCycle() {
      if (this.activeCycleID) {
        this.selectedCycle = this.activeCycleID
      }
      return
    },
    formatDate(d) {
      d = new Date(d)
      const yyyy = d.getFullYear();
      let mm = d.getMonth() + 1;
      let dd = d.getDate();

      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;

      const formattedDate = dd + '.' + mm + '.' + yyyy;
      return formattedDate
    },
    timeInRole,
    getTooltip(category, type) {
      if (this.types[category] && this.types[category][type]) {
        return this.types[category][type]
      }
      return type
    },
    getExpectedScoreVariant(expectedScore) {
      if (expectedScore === 2) return 'light-info'
      if (expectedScore === 3) return 'info'
      if (expectedScore === 4) return 'light-primary'
      if (expectedScore === 5) return 'primary'
      return 'primary'
    },
    getBadgeVariant(score, expectedScore) {
      const threshold = -0.25
      const diff = score - expectedScore;
      if (diff >= 0) return 'success'
      if (diff > threshold) return 'warning'
      if (diff <= threshold) return 'danger'
      return 'secondary'
    },
    goToUserResults(user) {
      if (this.isAdmin) {
        this.$router.push({ name: 'users-results', params: { id: user._id } })
      }
    },
    async downloadExcel() {
      if (!this.selectedCycle) return
      this.isExporting = true
      try {
        const response = await this.$http.get(`/results/export/${this.selectedCycle}`, {
          responseType: 'blob',
        })
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const cycleName = this.selectedCycleData ? this.selectedCycleData.title : 'Results'
        link.download = `Results - ${cycleName}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('Error downloading export:', err)
      } finally {
        this.isExporting = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-dot--active {
  background-color: rgb(0, 165, 0);
}

.status-dot--inactive {
  background-color: rgba(255, 0, 0, 0.76);
}

.edit-button {
  color: $primary;
  cursor: pointer;
}

.details-button {
  color: $primary;
  cursor: pointer;
}

.flag-red {
  background-color: red;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.flag-orange {
  background-color: orange;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.flag-green {
  background-color: green;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.flag-default {
  background-color: lightgrey;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
</style>
